import { memo, type ReactNode } from 'react';

import { ClipType } from '@air/api/types';
import { getExtStyles, ImageContainer } from '@air/component-file-extension-preview';

import {
  WorkspaceAgentPillThumbnail,
  type WorkspaceAgentPillThumbnailLayout,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillThumbnail';
import {
  WORKSPACE_AGENT_PANEL_PILL_THUMBNAIL,
  type WorkspaceAgentPanelImageDimensions,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { WorkspaceAgentAssetTypeIcon } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetTypeIcon';
import { isWorkspaceAgentAssetClipType } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentAssetClipType';

export type WorkspaceAgentPillLeadingVisualProps = {
  assetType: ClipType | null;
  ext: string | null;
  pillThumbnailTestId?: string;
  thumbnailDimensions?: WorkspaceAgentPanelImageDimensions;
  thumbnailLayout?: WorkspaceAgentPillThumbnailLayout;
  thumbnailUrl: string | null;
};

/** Still when present; otherwise ext tile or type glyph — shared by composer and history asset pills. */
export const WorkspaceAgentPillLeadingVisual = memo(
  ({
    assetType,
    ext,
    pillThumbnailTestId,
    thumbnailDimensions = WORKSPACE_AGENT_PANEL_PILL_THUMBNAIL,
    thumbnailLayout = 'pill',
    thumbnailUrl,
  }: WorkspaceAgentPillLeadingVisualProps) => {
    const resolvedType = isWorkspaceAgentAssetClipType(assetType) ? assetType : ClipType.nonMedia;

    let fallback: ReactNode;

    if (ext) {
      const { iconColor, iconComponent: IconComponent, iconName } = getExtStyles(ext);

      fallback = (
        <ImageContainer className="size-full overflow-hidden" color="color" ext={ext}>
          <span className="flex size-full items-center justify-center" style={{ color: iconColor }}>
            {/* 14px in the 18×20 leading block — `FileExtensionPreview` `small` is 20px and overflows. */}
            <IconComponent aria-hidden data-testid={iconName} size={14} />
          </span>
        </ImageContainer>
      );
    } else {
      fallback = <WorkspaceAgentAssetTypeIcon size="xs" type={resolvedType} />;
    }

    return (
      <WorkspaceAgentPillThumbnail
        dimensions={thumbnailDimensions}
        fallback={fallback}
        // Only over a still: on the fallback the scrim would bury the type glyph it sits on.
        hasPlayOverlay={!!thumbnailUrl && resolvedType === ClipType.video}
        layout={thumbnailLayout}
        testId={pillThumbnailTestId}
        url={thumbnailUrl}
      />
    );
  },
);

WorkspaceAgentPillLeadingVisual.displayName = 'WorkspaceAgentPillLeadingVisual';
